import React, { useState, useMemo, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import axiosInstance from '../../../services/api/axios';
import { useUser } from '../../../context/UserContext';
import { format, subDays, startOfWeek } from 'date-fns';
import { Typography, Card, CardBody, Collapse } from '@material-tailwind/react';
import { Line, Pie } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { ChevronDown, ChevronUp } from 'lucide-react';
import CustomInput from '../../../components/CustomInput';

Chart.register(...registerables);

function AgentDetails() {
    const [salesData, setSalesData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { userInfo } = useUser();
    const customerId = userInfo.loginIdentifierDetails.customer.customerId;
    const [dateFilters, setDateFilters] = useState({
        startDate: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
        endDate: format(new Date(), 'yyyy-MM-dd'),
    });
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const fetchSalesDetails = async () => {
            setLoading(true);
            setError(null);
            try {
                const params = new URLSearchParams({
                    customer_id: customerId,
                    start_date: dateFilters.startDate,
                    'end-date': dateFilters.endDate,
                    is_agent: true // Pass is_agent as true for AgentDetails
                });

                const performanceResponse = await axiosInstance.get(
                    `/customer/sales/performance?${params.toString()}`
                );
                console.log("agent perf", performanceResponse);


                const agentDataArray = Array.isArray(performanceResponse) ? performanceResponse : [];
                console.log("agentarray", agentDataArray);

                const agentIds = agentDataArray.map(item => item.entity.customerId);
                console.log("agent ids", agentIds);



                const salesParams = new URLSearchParams();
                agentIds.forEach(agentId => salesParams.append('agentIds', agentId.toString()));


                console.log("agent ids2 ", agentIds);
                console.log("sales params 2", salesParams.toString());


                let agentResponse = [];

                if (agentIds.length === 0) {
                    setSalesData(agentResponse);
                } else {
                    const agentResponseRaw = await axiosInstance.get(`/customer/sales?${salesParams.toString()}`);
                    // Handle both paginated and non-paginated responses
                    agentResponse = agentResponseRaw?.items || (Array.isArray(agentResponseRaw) ? agentResponseRaw : []);
                }


                console.log("agent sales", agentResponse);

                // Merge the data
                const mergedAgentData = agentResponse.map(sale => {
                    const salespersonInfo = agentDataArray.find(item => item.entity.customerId === sale.customerId);
                    console.log(salespersonInfo)

                    return {
                        ...sale,
                        agentName: salespersonInfo ? salespersonInfo.entity.ownerName : 'N/A',
                    };
                });

                setSalesData(mergedAgentData);
                console.log("agent Details:", mergedAgentData);
            } catch (e) {
                setError(`Error fetching sales details: ${e.message}`);
                console.error("Error fetching sales details:", e);
            } finally {
                setLoading(false);
            }
        };

        fetchSalesDetails();
    }, [customerId, dateFilters]);

    const filteredAgents = useMemo(() => {
        if (!searchQuery) return salesData;
        const lowerCaseQuery = searchQuery.toLowerCase();
        return salesData.filter(item =>
            item.agent.ownerName?.toLowerCase().includes(lowerCaseQuery) ||
            item.propertyData?.project?.projectName?.toLowerCase().includes(lowerCaseQuery) ||
            item.propertyData?.propertyNo?.toLowerCase().includes(lowerCaseQuery) ||
            (item.user?.firstName + ' ' + item.user?.lastName)?.toLowerCase().includes(lowerCaseQuery)
        );
    }, [salesData, searchQuery]);

    const columnDefs = useMemo(() => [
        { headerName: "Agent Name", field: "agent.ownerName", valueFormatter: (params) => params.value || 'N/A' },
        { headerName: "Project Name", field: "propertyData.project.projectName", valueFormatter: (params) => params.value || 'N/A' },
        { headerName: "Apartment No", field: "propertyData.propertyNo", valueFormatter: (params) => params.value || 'N/A' },
        {
            headerName: "Customer Name",
            field: "user",
            valueFormatter: (params) => {
                const user = params.value;
                if (!user) return 'N/A';
                return `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A';
            }
        },
        { headerName: "Base Value", field: "basePrice", valueFormatter: (params) => params.value || 'N/A' },
        { headerName: "Sale Date", field: "saleDate", valueFormatter: (params) => params.value ? format(new Date(params.value), 'yyyy-MM-dd') : 'N/A' },
    ], []);

    const handleDateChange = (e) => {
        e.stopPropagation();
        setDateFilters({
            ...dateFilters,
            [e.target.name]: e.target.value,
        });
    };

    const handleSearchChange = (e) => {
        e.stopPropagation();
        setSearchQuery(e.target.value);
    };

    return (
        <div>
            {/* Date Range and Search */}
            <div className="my-4 flex items-center justify-between">
                <CustomInput
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={handleSearchChange}
                    style={{ height: 'px' }}
                />
                <div className="flex items-center space-x-2">
                    <input
                        type="date"
                        name="startDate"
                        value={dateFilters.startDate || ""}
                        onChange={handleDateChange}
                        className="p-2.5 border border-gray-300 rounded-md text-sm"
                    />

                    <input
                        type="date"
                        name="endDate"
                        value={dateFilters.endDate || ""}
                        onChange={handleDateChange}
                        className="p-2.5 border border-gray-300 rounded-md text-sm"
                    />
                </div>
            </div>
            {/* AgGrid Table */}
            {loading ? (
                <Typography>Loading sales details...</Typography>
            ) : error ? (
                <Typography color="red">{error}</Typography>
            ) : (
                <div className="ag-theme-quartz font-normal my-6" style={{ height: '500px', width: '100%' }}>
                    <AgGridReact
                        columnDefs={columnDefs}
                        rowData={filteredAgents}
                        defaultColDef={{
                            flex: 1,
                            minWidth: 150,
                            resizable: true,
                            sortable: true,
                            filter: true,
                        }}
                    />
                </div>
            )}
        </div>
    );
}

export default AgentDetails;